/**
 * `beginLogout()` — sign out under the cookie-auth deployment.
 *
 * The colocation infra routes `/logout` through the furnace-router
 * ALB with `OnUnauthenticatedRequest: allow` (since the user may
 * already be partially signed out). The ALB:
 *
 *   1. Clears the `AWSELBAuthSessionCookie-*` shards
 *   2. Redirects the browser to Cognito's hosted-UI `/logout`
 *      endpoint with `logout_uri` pointing back at
 *      `foundry-staging.weekend.com`
 *   3. Cognito clears its own session
 *   4. Browser lands back at `/`; SPA boots fresh, `/crucible/me`
 *      returns 401, signed-out shell renders
 *
 * No `/oauth2/revoke` call from the SPA. No worker teardown. The
 * cookie is the only piece of identity state and the ALB owns it.
 *
 * Cross-tab sync: BEFORE the redirect we post a `{kind:'auth:cleared'}`
 * message on `BroadcastChannel('foundry-auth')` so any other tab open
 * at `phase:'ready'` flips to `signed-out` immediately, instead of
 * staying zombied until its next request to the runtime fails 401.
 * Best-effort; missing BroadcastChannel support falls through silently
 * and passive tabs degrade on their own.
 */

const AUTH_BROADCAST_CHANNEL = 'foundry-auth';

/** Payload posted by `beginLogout` + recognised by the cookie-boot listener. */
export interface AuthClearedMessage {
  kind: 'auth:cleared';
}

export interface LogoutDependencies {
  /** Top-level navigation. Real impl is `window.location.assign`. */
  redirect: (url: string) => void;
  /**
   * Post the cross-tab logout signal. Default impl uses
   * `BroadcastChannel('foundry-auth')`. Tests inject a recorder so
   * they don't need a real BroadcastChannel.
   */
  broadcastAuthCleared: () => void;
}

/**
 * Default dependencies — real browser globals.
 */
function defaultLogoutDependencies(): LogoutDependencies {
  return {
    redirect: (url) => {
      window.location.assign(url);
    },
    broadcastAuthCleared: () => {
      const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      const message: AuthClearedMessage = { kind: 'auth:cleared' };
      channel.postMessage(message);
      channel.close();
    },
  };
}

/**
 * Navigate to `/logout`, which triggers the ALB-and-Cognito sign-out
 * round-trip. Returns `never` because the assign unloads the
 * document.
 *
 * Order matters: broadcast FIRST, then redirect. The redirect can
 * unload the document at any point after `assign` is called, and any
 * post-assign work is best-effort. Broadcasting first guarantees the
 * cross-tab signal queues on the event loop before unload can race
 * it.
 */
export function beginLogout(deps: LogoutDependencies = defaultLogoutDependencies()): never {
  try {
    deps.broadcastAuthCleared();
  } catch {
    // BroadcastChannel unavailable (legacy Safari pre-15.4, very old
    // browsers): passive tabs will degrade on their own when their
    // next request to the runtime gets 401. Don't fail the sign-out.
  }
  deps.redirect('/logout');
  throw new Error('beginLogout: unreachable — top-level redirect must unload');
}

export { AUTH_BROADCAST_CHANNEL };
